import { useState } from "react";
import { Share2, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useConversations } from "@/lib/chat";
import { UserAvatar } from "@/components/UserAvatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { STATUS_LABEL, TYPE_LABEL } from "@/lib/constants";

export function ShareToChatDialog({ item, onClose }) {
  const open = !!item;
  const [convs] = useConversations(open);
  const [target, setTarget] = useState("team");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await api.post("/chat/messages", {
        conv: target,
        text: note.trim(),
        content_ref: {
          id: item.id, title: item.title, status: item.status,
          content_type: item.content_type, account_key: item.account_key,
          media_url: item.media_url || "", video_url: item.video_url || "",
        },
      });
      toast.success("Beitrag im Chat geteilt 📌");
      setNote("");
      setTarget("team");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="share-chat-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-blue-600" /> Im Chat teilen
          </DialogTitle>
          <DialogDescription className="truncate">
            „{item?.title}" · {STATUS_LABEL[item?.status] || item?.status} · {TYPE_LABEL[item?.content_type] || ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">An wen?</label>
            <div className="max-h-56 overflow-y-auto rounded-xl border border-neutral-200 divide-y divide-neutral-100">
              {convs.map((c) => {
                const isTeam = c.type === "team";
                const key = c.conv;
                return (
                  <button
                    key={key}
                    data-testid={`share-target-${isTeam ? "team" : c.user.id}`}
                    onClick={() => setTarget(key)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${
                      target === key ? "bg-blue-50" : "hover:bg-neutral-50"
                    }`}
                  >
                    {isTeam ? (
                      <span className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center shrink-0">
                        <Users className="h-4 w-4" />
                      </span>
                    ) : (
                      <UserAvatar src={c.user.avatar_url} name={c.name} color={c.user.avatar_color} className="h-8 w-8" textClass="text-[10px]" />
                    )}
                    <span className="flex-1 font-medium text-neutral-800">{isTeam ? "Team-Kanal" : c.name}</span>
                    <span
                      className={`h-4 w-4 rounded-full border-2 shrink-0 ${
                        target === key ? "border-blue-600 bg-blue-600" : "border-neutral-300"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1.5 block">Nachricht (optional)</label>
            <Textarea
              data-testid="share-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="z.B. Schaut euch das Reel an – was würdet ihr ändern?"
            />
          </div>
          <Button data-testid="share-submit-btn" onClick={send} disabled={sending} className="w-full">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
            Teilen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
